/** @jsxImportSource @emotion/react */
import {
  createStyles,
  Modal,
  Typography,
  useTheme,
} from '@quickbit/qb-design-system';
import { useBatchDeleteCases } from 'api/useCasesApi';

interface Props {
  caseIds: string[];
  onClose: () => void;
  onSuccess: () => void;
}

export const BatchDeleteModal = ({ caseIds, onClose, onSuccess }: Props) => {
  const styles = useStyles();
  const { palette } = useTheme();

  const batchDeleteMutation = useBatchDeleteCases();

  const handleDelete = () => {
    batchDeleteMutation.mutate(
      { caseIds },
      {
        onSuccess: (result) => {
          const failedCount = Object.values(result).filter((v) => !v).length;
          if (failedCount === 0) {
            onSuccess();
          }
        },
      }
    );
  };

  const successCount = batchDeleteMutation.data
    ? Object.values(batchDeleteMutation.data).filter((v) => v).length
    : 0;
  const failedCount = batchDeleteMutation.data
    ? Object.values(batchDeleteMutation.data).filter((v) => !v).length
    : 0;

  return (
    <Modal
      title="Delete Cases"
      closeModal={onClose}
      primaryButtonProps={{
        text: batchDeleteMutation.isPending
          ? 'Deleting...'
          : `Delete ${caseIds.length} case(s)`,
        onClick: handleDelete,
        disabled:
          batchDeleteMutation.isPending || batchDeleteMutation.isSuccess,
        loading: batchDeleteMutation.isPending,
      }}
      secondaryButtonProps={{
        text: batchDeleteMutation.isSuccess ? 'Close' : 'Cancel',
        onClick: batchDeleteMutation.isSuccess ? onSuccess : onClose,
      }}
    >
      <div css={styles.container}>
        {!batchDeleteMutation.data && (
          <Typography variant="body">
            Are you sure you want to delete {caseIds.length} case(s)? This
            action cannot be undone.
          </Typography>
        )}
        {batchDeleteMutation.data && (
          <>
            <Typography variant="body">
              Successfully deleted {successCount} case(s).
            </Typography>
            {failedCount > 0 && (
              <Typography variant="bodySmall" color={palette.danger.default}>
                Failed to delete {failedCount} case(s).
              </Typography>
            )}
          </>
        )}
        {batchDeleteMutation.error && (
          <Typography variant="bodySmall" color={palette.danger.default}>
            {batchDeleteMutation.error.errorMessage ?? 'Failed to delete cases'}
          </Typography>
        )}
      </div>
    </Modal>
  );
};

const useStyles = createStyles(({ spacing }) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
  },
}));
